import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plug, Plus, Trash2, Loader2, Copy, KeyRound, X } from "lucide-react";
import api, { API, formatError } from "@/lib/api";

const API_BASE = API.startsWith("http") ? API : `${window.location.origin}${API}`;

const ACESSO = {
  espaco: "Lê só o seu espaço",
  tudo: "Lê toda a memória",
};

function ChaveNova({ app, chave, onClose }) {
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(chave);
      toast.success("Chave copiada");
    } catch {
      toast.error("Não foi possível copiar; seleciona e copia à mão");
    }
  };
  return (
    <div data-testid="app-chave-nova" className="mt-6 p-4 rounded-xl border border-amber-500/40 bg-amber-500/5">
      <div className="flex items-start gap-3">
        <KeyRound size={18} className="text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-200 font-semibold">Chave de “{app}”</p>
          <p className="text-xs text-slate-400 mt-1">
            Copia-a agora e guarda-a na configuração da app. Por segurança, não volta a ser mostrada.
          </p>
          <div className="mt-3 flex gap-2">
            <code className="flex-1 min-w-0 truncate px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-xs font-mono text-slate-200">
              {chave}
            </code>
            <button onClick={copiar} className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold flex items-center gap-1.5">
              <Copy size={14} /> Copiar
            </button>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300" aria-label="Fechar">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default function AppsPage() {
  const [apps, setApps] = useState([]);
  const [nome, setNome] = useState("");
  const [acesso, setAcesso] = useState("espaco");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nova, setNova] = useState(null); // { app, chave }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/apps");
      setApps(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const criar = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/apps", { nome: nome.trim(), acesso });
      setNova({ app: data.nome, chave: data.chave });
      setNome("");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const novaChave = async (a) => {
    if (!window.confirm(`Gerar uma chave nova para “${a.nome}”? A atual deixa de funcionar de imediato.`)) return;
    const { data } = await api.post(`/apps/${a.id}/nova-chave`);
    setNova({ app: a.nome, chave: data.chave });
    load();
  };

  const revogar = async (a) => {
    if (!window.confirm(`Desligar “${a.nome}”? A chave deixa de funcionar. A memória do espaço fica guardada.`)) return;
    await api.delete(`/apps/${a.id}`);
    setApps((l) => l.filter((x) => x.id !== a.id));
    toast.success("App desligada");
  };

  return (
    <div className="h-full overflow-y-auto px-4 lg:px-8 py-8 pl-14 lg:pl-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
            <Plug size={20} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Apps ligadas</h1>
            <p className="text-sm text-slate-400">
              Outras aplicações (como a BigSolutions) guardam e consultam memória aqui, cada uma no seu espaço, e podem
              perguntar aos teus agentes.
            </p>
          </div>
        </div>

        <form onSubmit={criar} className="mt-6 flex flex-col sm:flex-row gap-2">
          <input
            data-testid="app-nome-input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Nome da app, ex.: BigSolutions"
            className="flex-1 px-4 py-2.5 rounded-lg bg-slate-900/70 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
          />
          <select
            data-testid="app-acesso-select"
            value={acesso}
            onChange={(e) => setAcesso(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-slate-900/70 border border-slate-800 text-sm text-slate-300"
          >
            <option value="espaco">{ACESSO.espaco}</option>
            <option value="tudo">{ACESSO.tudo}</option>
          </select>
          <button
            data-testid="app-criar-button"
            disabled={busy || !nome.trim()}
            className="px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Ligar app
          </button>
        </form>

        {nova && <ChaveNova app={nova.app} chave={nova.chave} onClose={() => setNova(null)} />}

        <div className="mt-6 space-y-2">
          {loading && <div className="text-slate-500 text-sm font-mono">a carregar…</div>}
          {!loading && apps.length === 0 && (
            <div className="text-center py-14 border border-dashed border-slate-800 rounded-2xl">
              <p className="text-slate-500 text-sm">Ainda não há apps ligadas.</p>
            </div>
          )}
          {apps.map((a) => (
            <div key={a.id} data-testid="app-item" className="p-4 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-semibold text-slate-100">{a.nome}</span>
                    <span className="text-[11px] font-mono text-slate-500">espaço: {a.espaco}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {ACESSO[a.acesso] || a.acesso} · {a.memorias} memórias · {a.factos} factos · chave {a.chave_inicio}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {a.ultimo_uso ? `último uso ${new Date(a.ultimo_uso).toLocaleString("pt-PT")}` : "ainda não foi usada"}
                  </div>
                </div>
                <button onClick={() => novaChave(a)} title="Gerar chave nova" className="text-slate-500 hover:text-amber-400">
                  <KeyRound size={16} />
                </button>
                <button data-testid="app-revogar-button" onClick={() => revogar(a)} title="Desligar" className="text-slate-500 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-4 rounded-xl border border-slate-800 text-xs text-slate-400 leading-relaxed">
          <p className="font-semibold text-slate-300 mb-1">Como a app se liga</p>
          <p>
            Envia pedidos para <code className="font-mono text-slate-300">{API_BASE}/v1/…</code> com o cabeçalho{" "}
            <code className="font-mono text-slate-300">X-API-Key: &lt;chave&gt;</code>. Aceita os formatos{" "}
            <code className="font-mono">super-memoria/1</code> e <code className="font-mono">bigsolutions-memoria/1</code>.
            O guia completo está em <code className="font-mono">docs/API-APPS.md</code>.
          </p>
        </div>
      </div>
    </div>
  );
}
